import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useMutation } from 'convex/react'
import { ArrowUpRight, Check, Keyboard, Mic, Quote, RotateCw, WifiOff, X } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CaptureCard, CaptureCardMotion, LiveCaptureItems } from '@/components/capture-card'
import GoalBuildLoader from '@/components/goal-build-loader'
import { ImageDropHint, ImagePickButton, useImagePasteDrop } from '@/components/image-capture'
import { MicLevelMeter } from '@/components/mic-level-meter'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useBrainDumpPrompt } from '@/hooks/use-brain-dump-prompt'
import { useIsPro } from '@/hooks/use-is-pro'
import { useIsMobile } from '@/hooks/use-mobile'
import { AUTO_STOP_MS, isVoiceCaptureSupported, useVoiceCapture } from '@/hooks/use-voice-goals'
import {
  countItems,
  countSummary,
  formatDuration,
  formatTimer,
  isCaptureHotkey,
  isMacPlatform,
} from '@/lib/capture'
import { removeNode } from '@/lib/nodes-optimistic'
import { undoToast } from '@/lib/undo-toast'
import { openCapture, useCaptureStore } from '@/store/capture.store'

const PROMPT_START_MS = 1500
const PROMPT_MAX_MS = 6000

const kbdClass =
  'rounded border border-border bg-muted px-1 py-px font-mono text-[10px] leading-none text-muted-foreground'

export function QuickCapture() {
  const { t } = useTranslation()
  const open = useCaptureStore((s) => s.open)
  const setOpen = useCaptureStore((s) => s.setOpen)
  const { isPro, isLoading } = useIsPro()
  const isMobile = useIsMobile()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const onboarding = pathname.startsWith('/onboarding')
  // /voice runs its own recognizer; a second session would fight it for the mic.
  const onVoicePage = pathname.startsWith('/voice')

  useEffect(() => {
    if (onboarding || onVoicePage) return
    const onKey = (e: KeyboardEvent) => {
      if (!isCaptureHotkey(e)) return
      e.preventDefault()
      if (!useCaptureStore.getState().open) openCapture()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onboarding, onVoicePage])

  const prompt = useBrainDumpPrompt(isPro && !onboarding)

  if (isLoading || onboarding) return null
  if (!isPro) return <UpgradeDialog open={open} onOpenChange={setOpen} reason="voice" />

  if (isMobile) {
    return (
      // Docked above the tab bar (which stays usable and shows Brain dump as the active tab), so
      // non-modal; a press on the tab bar's mic toggles it there instead of counting as outside.
      <Sheet
        open={open}
        modal={false}
        onOpenChange={(next, details) => {
          const target = details.event?.target
          if (!next && target instanceof Element && target.closest('[data-capture-toggle]')) {
            details.cancel()
            return
          }
          setOpen(next)
        }}
      >
        <SheetContent
          side="bottom"
          showCloseButton={false}
          // Grows out of the tab bar: flush on its top edge, same surface, no seam; the bar
          // (drawn above it) hides its top line and hump outline while this is open.
          className="flex max-h-[70dvh] flex-col overflow-hidden rounded-t-2xl bg-background px-5 pt-5 pb-10 shadow-[0_-12px_40px_rgba(0,0,0,0.35)] data-[side=bottom]:bottom-[calc(58px+env(safe-area-inset-bottom,0px))] data-[side=bottom]:border-t-0"
        >
          <SheetTitle className="sr-only">{t('voice.title')}</SheetTitle>
          <QuickCaptureBody docked takePrompt={prompt.take} onClose={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent showCloseButton={false} className="max-w-lg p-5">
        <DialogTitle className="sr-only">{t('voice.title')}</DialogTitle>
        <QuickCaptureBody takePrompt={prompt.take} onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

export const VOICE_STARTERS = ['starter1', 'starter2', 'starter3', 'starter4'] as const

export const starterPhrase = (text: string) => text.replace(/…+$/, '')

export function RecordingStatus({
  paused,
  elapsedMs,
  activity,
  meter = true,
  wrapUpIn = null,
}: {
  paused: boolean
  elapsedMs: number
  activity: string
  meter?: boolean
  wrapUpIn?: number | null
}) {
  const { t } = useTranslation()
  return (
    <span className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
      <span
        className={cn(
          'size-2 shrink-0 rounded-full',
          paused ? 'bg-muted-foreground' : 'animate-pulse bg-primary motion-reduce:animate-none',
        )}
      />
      {meter && <MicLevelMeter active={!paused} activity={activity} />}
      <span className="truncate">
        {paused ? t('voice.paused') : t('voice.listening')} ·{' '}
        <span className="tabular-nums">{formatTimer(elapsedMs)}</span>
        {wrapUpIn !== null ? (
          <span aria-live="polite" className="tabular-nums text-foreground/70">
            {' '}
            · {t('voice.wrappingUp', { count: wrapUpIn })}
          </span>
        ) : (
          <span className="text-muted-foreground/60">
            {' '}
            · {t('voice.upTo', { count: AUTO_STOP_MS / 60_000 })}
          </span>
        )}
      </span>
    </span>
  )
}

export function LiveTranscript({ text, className }: { text: string; className?: string }) {
  const ref = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [text])
  return (
    <p
      ref={ref}
      className={cn(
        'overflow-y-auto text-[17px] leading-relaxed whitespace-pre-wrap text-foreground',
        className,
      )}
    >
      {text}
    </p>
  )
}

export function StuckPrompts({
  onPick,
  className,
}: {
  onPick: (phrase: string) => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <span className="shrink-0 text-xs text-muted-foreground">{t('voice.stuck')}</span>
      <div className="-mr-5 flex min-w-0 gap-1.5 overflow-x-auto pr-5 lg:mr-0 lg:flex-wrap lg:pr-0">
        {VOICE_STARTERS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onPick(starterPhrase(t(`voice.${key}`)))}
            className="max-w-48 shrink-0 cursor-pointer truncate rounded-full border border-border bg-card px-2.5 py-1 text-xs text-card-foreground transition-colors hover:bg-muted/40"
          >
            {t(`voice.${key}`)}
          </button>
        ))}
      </div>
    </div>
  )
}

export function DoneButton({
  paused,
  onDone,
  onResume,
  className,
}: {
  paused: boolean
  onDone: () => void
  onResume: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return paused ? (
    <button
      type="button"
      onClick={onResume}
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98]',
        className,
      )}
    >
      <Mic className="h-4 w-4" />
      {t('voice.resume')}
    </button>
  ) : (
    <button
      type="button"
      onClick={onDone}
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded-full bg-destructive px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-destructive/90 active:scale-[0.98]',
        className,
      )}
    >
      <Check className="h-4 w-4" strokeWidth={2.5} />
      {t('voice.done')}
    </button>
  )
}

// `docked`: phone layout above the tab bar, whose mic is the record / stop control.
function QuickCaptureBody({
  onClose,
  takePrompt,
  docked = false,
}: {
  onClose: () => void
  takePrompt: () => HTMLAudioElement | null
  docked?: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const posthog = usePostHog()
  const voice = useVoiceCapture()
  const remove = useMutation(api.nodes.remove).withOptimisticUpdate((localStore, { nodeId }) =>
    removeNode(localStore, nodeId),
  )
  const setBusy = useCaptureStore((s) => s.setBusy)
  const setMic = useCaptureStore((s) => s.setMic)
  const [conversation] = useState(() => useCaptureStore.getState().conversation)
  const source = conversation ? (conversation.since ? 'call' : 'chat') : 'quick'
  const extractedRef = useRef(false)
  const [voiceSupported] = useState(isVoiceCaptureSupported)
  const [typing, setTyping] = useState(!voiceSupported)
  const [text, setText] = useState('')
  const [creating, setCreating] = useState(false)
  const [prompting, setPrompting] = useState(false)
  const promptRef = useRef<HTMLAudioElement | null>(null)
  const voiceRef = useRef(voice)
  voiceRef.current = voice
  const { status } = voice
  const recording = status === 'listening' || status === 'paused'
  const mac = isMacPlatform()

  const [beat, setBeat] = useState(0)
  useEffect(() => {
    if (!prompting) return
    const id = setInterval(() => setBeat((n) => n + 1), 220)
    return () => clearInterval(id)
  }, [prompting])

  function stopPrompt() {
    const audio = promptRef.current
    promptRef.current = null
    audio?.pause()
    setPrompting(false)
  }

  function start(prefix?: string) {
    stopPrompt()
    posthog.capture('voice_capture_started', { source: 'quick' })
    setTyping(false)
    voice.start(prefix)
  }

  function pickImage(file: File) {
    stopPrompt()
    posthog.capture('voice_capture_started', { source: 'image' })
    setTyping(false)
    void voice.submitImage(file)
  }

  const dragging = useImagePasteDrop(
    pickImage,
    !conversation && status !== 'processing' && status !== 'review',
  )
  const eventSource = voice.fromImage ? 'image' : source

  // The coach's spoken opener must finish before recognition starts, or the mic transcribes it.
  useEffect(() => {
    if (conversation) {
      // StrictMode re-runs this effect; one extraction is one brain-dump use.
      if (extractedRef.current) return
      extractedRef.current = true
      posthog.capture('voice_capture_started', { source })
      void voice.extractConversation(conversation).then((found) => {
        if (!found) onClose()
      })
      return () => setBusy(false)
    }
    const audio = voiceSupported ? takePrompt() : null
    if (!audio) {
      if (voiceSupported) start()
      return () => setBusy(false)
    }
    promptRef.current = audio
    setPrompting(true)
    const go = () => {
      if (promptRef.current === audio) start()
    }
    let timer = setTimeout(go, PROMPT_START_MS)
    audio.onplaying = () => {
      clearTimeout(timer)
      timer = setTimeout(go, PROMPT_MAX_MS)
    }
    audio.onended = go
    audio.onerror = go
    audio.play().catch(go)
    return () => {
      clearTimeout(timer)
      audio.onplaying = audio.onended = audio.onerror = null
      audio.pause()
      setBusy(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!docked) return
    const mic =
      status === 'listening'
        ? 'listening'
        : status === 'paused'
          ? 'paused'
          : !typing && (status === 'idle' || status === 'error')
            ? 'ready'
            : null
    setMic(
      mic,
      mic === 'listening'
        ? () => voiceRef.current.stop()
        : mic === 'paused'
          ? () => voiceRef.current.resume()
          : mic === 'ready'
            ? () => start()
            : null,
    )
  }, [docked, status, typing]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => setMic(null, null), [setMic])

  useEffect(() => {
    if (!recording || typing) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing || e.shiftKey || e.metaKey || e.ctrlKey) return
      e.preventDefault()
      voiceRef.current.stop()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [recording, typing])

  async function create() {
    setCreating(true)
    setBusy(true)
    const { counts, ids } = await voice.createSelected()
    setBusy(false)
    setCreating(false)
    posthog.capture('voice_capture_created', { ...counts, source: eventSource })
    const summary = countSummary(t, counts)
    if (!summary) return
    onClose()
    undoToast(
      t('voice.addedToast', { summary }),
      () => {
        Promise.all(ids.map((nodeId: Id<'nodes'>) => remove({ nodeId }))).catch(() =>
          toast.error(t('common.error')),
        )
      },
      6000,
    )
  }

  useEffect(() => {
    if (status !== 'review') return
    posthog.capture('voice_goals_extracted', {
      count: voice.items.length,
      ...countItems(voice.items),
      source: eventSource,
    })
  }, [status]) // eslint-disable-line react-hooks/exhaustive-deps

  function switchToTyping() {
    stopPrompt()
    voice.pause()
    setText(voice.transcript)
    setTyping(true)
  }

  function openFullView() {
    onClose()
    void navigate({ to: '/voice' })
  }

  // The full /voice page's opener, inline: a prompt to talk plus phrases that start the dump.
  const starters = (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed text-muted-foreground">{t('voice.lead')}</p>
      <div className="grid gap-1.5">
        {VOICE_STARTERS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => start(starterPhrase(t(`voice.${key}`)))}
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-border bg-card px-3.5 py-2.5 text-left text-sm text-card-foreground transition-colors hover:bg-muted/40"
          >
            <Quote className="h-3.5 w-3.5 shrink-0 text-primary-text" />
            {t(`voice.${key}`)}
          </button>
        ))}
      </div>
    </div>
  )

  const fullViewButton = (
    <button
      type="button"
      onClick={openFullView}
      className="flex cursor-pointer items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      {t('voice.openFullView')}
      <ArrowUpRight className="h-3.5 w-3.5" />
    </button>
  )

  if (dragging) return <ImageDropHint />

  if (status === 'processing') {
    return (
      <div className="min-h-32">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          {t(
            voice.reconnecting
              ? 'voice.reconnecting'
              : conversation
                ? 'voice.readingConversation'
                : voice.fromImage
                  ? 'voice.readingImage'
                  : 'voice.extracting',
          )}
        </div>
        {voice.liveItems.length > 0 ? (
          <LiveCaptureItems items={voice.liveItems} />
        ) : (
          voice.transcript && (
            <p className="mt-4 max-h-[30vh] overflow-y-auto text-base leading-relaxed whitespace-pre-wrap text-foreground/40">
              {voice.transcript}
            </p>
          )
        )}
      </div>
    )
  }

  if (status === 'review' && voice.items.some((i) => i.kind === 'goal' && i.status === 'creating'))
    return <GoalBuildLoader compact />

  if (status === 'review') {
    const selected = voice.items.filter(
      (i) => i.selected && i.status === 'pending' && i.title.trim(),
    )
    const selectedCount = selected.length
    const summary = countSummary(t, countItems(voice.items))
    const selectedSummary = countSummary(t, countItems(selected))
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <p className="shrink-0 text-xs text-muted-foreground">
          {voice.spokenMs !== null
            ? t('voice.fromTalking', { duration: formatDuration(t, voice.spokenMs), summary })
            : voice.fromImage
              ? t('voice.fromImageSummary', { summary })
              : summary}
        </p>
        <h2 className="mt-0.5 shrink-0 text-lg font-black text-foreground">
          {t('voice.reviewTitle')}
        </h2>
        <ul
          className={cn(
            'mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain',
            !docked && 'max-h-[50vh]',
          )}
        >
          {voice.items.map((item) => (
            <CaptureCardMotion key={item.key} layoutKey={item.liveKey} appear={!item.liveKey}>
              <CaptureCard
                item={item}
                relatedEvent={
                  item.relatedEventIndex === undefined
                    ? null
                    : voice.items.find((i) => i.key === item.relatedEventIndex)
                }
                dense
                disabled={creating}
                onToggle={(selected) => voice.toggle(item.key, selected)}
                onRename={(title) => voice.rename(item.key, title)}
                onWhen={(when) => voice.setWhen(item.key, when)}
                onEdit={(p) => voice.editTask(item.key, p)}
              />
            </CaptureCardMotion>
          ))}
        </ul>
        {selectedSummary && (
          <p className="mt-3 shrink-0 font-mono text-[11px] text-muted-foreground">
            {selectedSummary}
          </p>
        )}
        <div className={cn('flex shrink-0 gap-2', selectedSummary ? 'mt-2' : 'mt-4')}>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="cursor-pointer rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('voice.discard')}
          </button>
          <button
            type="button"
            onClick={() => void create()}
            disabled={creating || selectedCount === 0}
            className="flex-1 cursor-pointer rounded-full bg-primary py-2.5 text-center text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creating ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('voice.creating')}
              </span>
            ) : (
              t('voice.createCount', { count: selectedCount })
            )}
          </button>
        </div>
      </div>
    )
  }

  if (typing) {
    return (
      <div>
        <textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) {
              e.preventDefault()
              voice.submitText(text)
            }
          }}
          placeholder={t('voice.typePlaceholder')}
          rows={5}
          className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 text-base text-card-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
        />
        {!voiceSupported && (
          <p className="mt-1 text-xs text-muted-foreground">{t('voice.typeOnlyNote')}</p>
        )}
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="flex items-center gap-4">
            {voiceSupported ? (
              <button
                type="button"
                onClick={() => start(text)}
                className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <Mic className="h-4 w-4" />
                {t('voice.useVoice')}
              </button>
            ) : (
              !docked && fullViewButton
            )}
            <ImagePickButton onImage={pickImage} />
          </span>
          <button
            type="button"
            disabled={!text.trim()}
            onClick={() => voice.submitText(text)}
            className="flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('voice.continue')}
            <span className="hidden text-[10px] font-medium opacity-70 lg:inline">
              {mac ? '⌘⏎' : 'Ctrl⏎'}
            </span>
          </button>
        </div>
      </div>
    )
  }

  if (recording || prompting) {
    const paused = status === 'paused'
    return (
      <div onPointerDown={voice.nudge}>
        <div className="flex items-center justify-between gap-3">
          {recording ? (
            <RecordingStatus
              paused={paused}
              elapsedMs={voice.elapsedMs}
              activity={voice.transcript}
              meter={false}
              wrapUpIn={voice.wrapUpIn}
            />
          ) : (
            <span />
          )}
          <span className="flex items-center gap-3">
            <span className="hidden items-center gap-1 text-[11px] text-muted-foreground lg:flex">
              <kbd className={kbdClass}>Esc</kbd>
              {t('voice.kbdCancel')}
              <span className="px-0.5">·</span>
              <kbd className={kbdClass}>⏎</kbd>
              {t('voice.kbdDone')}
            </span>
            {/* Close discards: the sheet unmounts after its exit animation, which
                stops recording (resetting first made the sheet flicker mid-close). */}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('voice.cancel')}
              className="-mr-1.5 flex size-8 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </span>
        </div>
        <MicLevelMeter
          active={!paused}
          activity={prompting ? String(beat) : voice.transcript}
          height={voice.transcript ? 64 : 112}
          className="mx-auto mt-4 w-fit gap-2.5 transition-[height] duration-300"
          barClassName="w-3"
        />
        {voice.transcript && (
          <>
            <LiveTranscript
              text={voice.transcript}
              className={cn('mt-4', docked ? 'max-h-[24dvh]' : 'max-h-[30vh]')}
            />
            <div className={cn(docked && 'max-h-[22dvh] overflow-y-auto')}>
              <LiveCaptureItems items={voice.liveItems} />
            </div>
          </>
        )}
        <div className="mt-5 flex items-center gap-4">
          <button
            type="button"
            onClick={switchToTyping}
            className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <Keyboard className="h-4 w-4" />
            {t('voice.typeInstead')}
          </button>
          <ImagePickButton onImage={pickImage} iconOnly={!docked} />
          {!docked && fullViewButton}
          {/* Docked: Done/Resume live on the tab bar's centre button. */}
          {!docked && recording && (
            <DoneButton
              paused={paused}
              onDone={voice.stop}
              onResume={voice.resume}
              className="ml-auto"
            />
          )}
        </div>
      </div>
    )
  }

  if (status === 'error' && voice.retry) {
    return (
      <div className="min-h-32 space-y-4">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{voice.errorMessage}</p>
        </div>
        {voice.transcript && (
          <p className="max-h-[30vh] overflow-y-auto text-base leading-relaxed whitespace-pre-wrap text-foreground/60">
            {voice.transcript}
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={voice.retry}
            className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <RotateCw className="h-4 w-4" />
            {t('voice.retry')}
          </button>
          <button
            type="button"
            onClick={voice.reset}
            className="cursor-pointer text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('voice.startOver')}
          </button>
        </div>
      </div>
    )
  }

  if (docked) {
    return (
      <div className="space-y-4">
        {status === 'error' && voice.errorMessage && (
          <p className="text-sm text-destructive">{voice.errorMessage}</p>
        )}
        {starters}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setTyping(true)}
            className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <Keyboard className="h-4 w-4" />
            {t('voice.typeInstead')}
          </button>
          <ImagePickButton onImage={pickImage} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-3 py-2 text-center">
      {status === 'error' && voice.errorMessage && (
        <p className="text-sm text-destructive">{voice.errorMessage}</p>
      )}
      {!docked && (
        <button
          type="button"
          onClick={() => start()}
          aria-label={t('voice.startRecording')}
          className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-primary transition-all duration-200 hover:bg-primary/90 active:scale-95"
        >
          <Mic className="h-6 w-6 text-primary-foreground" />
        </button>
      )}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setTyping(true)}
          className="cursor-pointer text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('voice.typeInstead')}
        </button>
        <ImagePickButton onImage={pickImage} />
        {fullViewButton}
      </div>
    </div>
  )
}
